import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TabsModule } from 'primeng/tabs';
import { filter } from 'rxjs/operators';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { MenuItemWithIcon } from '../../../../../../types/menu.interface';
import { PermissionScope } from '../../../../../../types/permission-scope.enum';
import { assetWriteSignal } from '../../../../../../utils/asset-permissions.utils';
import { assetIdSignalFromRoute } from '../../../../../../utils/asset-route.utils';
import { buildEntityMenuItems } from '../../../../../../utils/entity-menu.utils';
import { CesAssetBreadcrumbComponent } from '../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../../ui/ces-buttons/ces-button.component';
import { CesMenuButtonSecondaryComponent } from '../../../../../ui/ces-buttons/ces-menu-button-secondary.component';
import { CesTitleComponent } from '../../../../../ui/ces-route-title/ces-route-title.component';
import { CampaignModelActionsService } from '../sub-services/campaign-model-actions.service';
import { CampaignNavigationService } from '../sub-services/campaign-navigation.service';
import { CampaignModelPlanningTabComponent } from './campaign-model-planning-tab.component';

type ModelTab = 'measures' | 'auscultation';

@Component({
  selector: 'app-campaign-model-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    TabsModule,
    TranslocoModule,
    CesAssetBreadcrumbComponent,
    CesButtonComponent,
    CesMenuButtonSecondaryComponent,
    CesTitleComponent,
  ],
  templateUrl: './campaign-model-detail.component.html',
})
export class CampaignModelDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly actions = inject(CampaignModelActionsService);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  readonly nav = inject(CampaignNavigationService);

  private readonly tabRoutes: ModelTab[] = ['measures', 'auscultation'];
  readonly activeIndex = signal(0);

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  readonly canWriteAsset = assetWriteSignal(PermissionScope.Campaigns, this.currentAssetId);

  // Planning grid of the active tab; the header hosts its plan/unplan actions
  // (same outlet-component bridge as the campaign detail page).
  private readonly activeTab = signal<CampaignModelPlanningTabComponent | null>(null);
  readonly planningGrid = computed(() => this.activeTab()?.planning() ?? null);

  onTabActivate(component: unknown): void {
    this.activeTab.set(component instanceof CampaignModelPlanningTabComponent ? component : null);
  }

  onTabDeactivate(): void {
    this.activeTab.set(null);
  }

  // The active tab owns the catalogue dialog; the header just triggers it.
  openPlanningDialog(): void {
    this.activeTab()?.openPlanningDialog();
  }

  private readonly modelId = Number(this.route.snapshot.paramMap.get('modelId'));
  readonly model = computed(() => this.campaignModelStore.getById(this.modelId) ?? null);

  readonly menuItems = computed((): MenuItemWithIcon[] => {
    this.translations();
    return buildEntityMenuItems(
      this.translate,
      'common.buttons',
      {
        onEdit: () => void this.onRename(),
        onDuplicate: () => void this.onDuplicate(),
        onDelete: () => void this.onDelete(),
      },
      { canWrite: this.canWriteAsset() },
    );
  });

  ngOnInit(): void {
    this.activeIndex.set(this.getTabIndexFromUrl(this.router.url));
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.activeIndex.set(this.getTabIndexFromUrl(event.url));
      });

    void this.loadModel();
  }

  private async loadModel(): Promise<void> {
    try {
      const model = await this.campaignModelStore.getByIdAsync(this.modelId);
      if (!model) void this.router.navigate(['/404']);
    } catch {
      void this.router.navigate(['/404']);
    }
  }

  private getTabIndexFromUrl(url: string): number {
    const tab = this.tabRoutes.find(candidate => url.endsWith(`/${candidate}`));
    return tab ? this.tabRoutes.indexOf(tab) : 0;
  }

  onTabChange(index: number): void {
    const tabRoute = this.tabRoutes[index];
    if (!tabRoute) return;
    this.activeIndex.set(index);
    void this.router.navigate([tabRoute], { relativeTo: this.route });
  }

  private async onRename(): Promise<void> {
    const model = this.model();
    if (model) await this.actions.rename(model);
  }

  private async onDuplicate(): Promise<void> {
    const model = this.model();
    if (!model) return;
    const siblings = this.campaignModelStore.entities().map(m => m.name ?? '');
    const copy = await this.actions.duplicate(model, siblings);
    if (copy) {
      void this.router.navigate(['/assets', this.currentAssetId(), 'monitoring', 'campaigns', 'models', copy.id, 'measures']);
    }
  }

  private async onDelete(): Promise<void> {
    const model = this.model();
    if (!model) return;
    const deleted = await this.actions.delete(model);
    if (deleted) {
      void this.router.navigate(['/assets', this.currentAssetId(), 'monitoring', 'campaigns', 'models']);
    }
  }
}
